import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { STORAGE_KEYS } from '../../shared/constants/storage-keys';
import { AuditLog } from '../../shared/models/audit/audit-log';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  public languageChangeSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
    this.getLanguage() ?? TranslationService.defaultLanguageCode()
  );

  private languageCodeId: string | null = null;

  public static defaultLanguageCode(): string {
    return 'de-DE';
  }

  /**
   * Two-letter language code (ISO 639-1) derived from a language code id, e.g. 'de' for 'de-DE'.
   */
  public static toLanguageCode(languageCodeId: string): string {
    return languageCodeId.split('-')[0];
  }

  public setLanguage(languageCodeId: string): void {
    if (this.languageCodeId === languageCodeId) {
      return;
    }

    this.languageCodeId = languageCodeId;
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, languageCodeId);
    this.languageChangeSubject.next(languageCodeId ?? TranslationService.defaultLanguageCode());
  }

  public getLanguage(): string | null {
    if (this.languageCodeId) {
      return this.languageCodeId;
    } else {
      const languageCodeId = localStorage.getItem(STORAGE_KEYS.LANGUAGE);

      if (languageCodeId) {
        this.languageCodeId = languageCodeId;
        return languageCodeId;
      } else {
        return TranslationService.defaultLanguageCode();
      }
    }
  }

  public defaultLanguageSelected(): boolean {
    return this.getLanguage() == TranslationService.defaultLanguageCode();
  }

  public processAuditLogsForCurrentLanguage(auditLogs: AuditLog[]): AuditLog[] {
    const currentLanguage = this.getLanguage();
    if (currentLanguage == TranslationService.defaultLanguageCode()) {
      // simplify audit log entries related to translations of other languages
      return auditLogs.map((a) => this.simplifyTranslationLogEntryForOtherLanguages(a));
    }

    // remove log entries related to translations of other languages
    return auditLogs.filter((a) =>
      a.entityName?.endsWith('Translation') ? a.languageCodeId == currentLanguage : true
    );
  }

  private simplifyTranslationLogEntryForOtherLanguages(auditLog: AuditLog): AuditLog {
    if (!auditLog.entityName?.endsWith('Translation')) {
      return auditLog;
    }

    // remove details
    auditLog.changedProperties = '';
    return auditLog;
  }
}
